import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Pressable, StyleSheet, View, useWindowDimensions } from 'react-native';
import {
  ActivityIndicator,
  Button,
  Divider,
  IconButton,
  Text,
  TextInput,
  useTheme,
} from 'react-native-paper';
import { useTranslation } from 'react-i18next';

import type { PaywallConfig, PaywallFeature } from '@/src/types/paywall';
import type { BillingCycle, CatalogPlan, PricingBreakdown } from '@/src/features/onboarding/ownerPlanSelection';
import { apiFetch } from '@/src/api/apiService';
import { API_BASE } from '@/src/api/config';
import TrialTimeline from '@/components/paywall/TrialTimeline';
import {
  MAX_LICENSES,
  NARROW_BREAKPOINT,
  derivePhaseTwo,
  planFor,
  productNaming,
  purchasableCycles,
  quotableFeatures,
  summariseForPhaseOne,
  type Persona,
  type PricingPhase,
} from './productPricingPhases';

/**
 * Two-phase product/price surface, shared by owner and driver (#291).
 *
 * Returns TWO slots on purpose. The single-view predecessor was handed to the
 * chat's `composer`, a fixed footer sized for one input row — so on a 320 px
 * screen the catalogue, the totals and the CTA all lived outside the scroll
 * area and simply could not be reached. Here `content` belongs in the scrollable
 * surface and `footer` holds only the running total and the CTA, which is what
 * keeps the CTA reachable at every viewport.
 *
 * Phase 1 asks one question — which product — with only what is needed to answer
 * it. Phase 2 configures the chosen product. The sequence is identical on every
 * screen size; only the card layout changes with the breakpoint.
 */

export interface ProductPricingSelection {
  feature: PaywallFeature;
  billing: BillingCycle;
  plan: CatalogPlan;
  quantity: number;
  breakdown: PricingBreakdown;
}

export interface ProductPricingSurfaceOptions {
  catalog: CatalogPlan[];
  catalogLoading?: boolean;
  catalogError?: string | null;
  onRetryCatalog?: () => void;
  paywallConfig: PaywallConfig | null;
  paywallConfigLoading?: boolean;
  paywallConfigError?: string | null;
  persona: Persona;
  initialFeature?: PaywallFeature;
  initialQuantity?: number;
  onConfirm: (selection: ProductPricingSelection) => void | Promise<void>;
  confirmTestID?: string;
}

export interface ProductPricingSurface {
  phase: PricingPhase;
  /** Product currently chosen — the host renders it as the phase-1 answer. */
  selectedFeature: PaywallFeature;
  /** Belongs inside the scrollable area of the host screen. */
  content: ReactNode;
  /** Pinned footer: running total + CTA only. Null while choosing a product. */
  footer: ReactNode;
}

export function useProductPricingSurface({
  catalog,
  catalogLoading = false,
  catalogError = null,
  onRetryCatalog,
  paywallConfig,
  paywallConfigLoading = false,
  paywallConfigError = null,
  persona,
  initialFeature,
  initialQuantity = 1,
  onConfirm,
  confirmTestID = 'pricing-confirm',
}: ProductPricingSurfaceOptions): ProductPricingSurface {
  const { t, i18n } = useTranslation('onboarding');
  const theme = useTheme();
  const { width } = useWindowDimensions();
  const narrow = width < NARROW_BREAKPOINT;

  const [phase, setPhase] = useState<PricingPhase>(initialFeature ? 'configure' : 'product');
  const [feature, setFeature] = useState<PaywallFeature>(initialFeature ?? 'hours');
  const [billing, setBilling] = useState<BillingCycle>('monthly');
  const [quantity, setQuantity] = useState(String(Math.max(1, Math.round(initialQuantity))));
  // Whether the owner has taken control of the quantity. Until they do, the
  // suggestion from the declared fleet size still applies — this hook mounts
  // before that answer exists, so the initial value arrives late.
  const [quantityTouched, setQuantityTouched] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // D-057: read-only referral summary for the authenticated account. It only
  // ever DISPLAYS an attributed partner; there is never a code field here (a
  // referral can only be captured during registration) and the trial days come
  // from the server's plan, never a hard-coded number.
  const [referralPartner, setReferralPartner] = useState<string | null>(null);

  useEffect(() => {
    let current = true;
    (async () => {
      try {
        const response = await apiFetch<{ referred?: boolean; partner_name?: string | null }>(
          `${API_BASE}/api/referrals/claim/`,
          { includeCompanyContext: false },
          true,
          false,
        );
        if (!current) return;
        if (response.ok && response.data?.referred) setReferralPartner(response.data.partner_name || null);
      } catch {
        // The referral summary is optional: a rejected claim must never block
        // the pricing surface (D-012). Fall through showing no partner.
      }
    })();
    return () => { current = false; };
  }, []);

  useEffect(() => {
    if (quantityTouched) return;
    setQuantity(String(Math.max(1, Math.round(initialQuantity))));
  }, [initialQuantity, quantityTouched]);

  const available = useMemo(() => quotableFeatures(catalog), [catalog]);

  useEffect(() => {
    if (available.length > 0 && !available.includes(feature)) setFeature(available[0]);
  }, [available, feature]);

  const cycles = useMemo(() => purchasableCycles(catalog, feature), [catalog, feature]);
  useEffect(() => {
    if (cycles.length > 0 && !cycles.includes(billing)) setBilling(cycles[0]);
  }, [billing, cycles]);

  const parsedQuantity = Number.parseInt(quantity, 10);
  const quantityValid =
    Number.isInteger(parsedQuantity) && parsedQuantity >= 1 && parsedQuantity <= MAX_LICENSES;

  const state = useMemo(
    () =>
      derivePhaseTwo({
        catalog,
        feature,
        billing,
        quantity: quantityValid ? parsedQuantity : Number.NaN,
        persona,
        paywallConfig,
      }),
    [billing, catalog, feature, parsedQuantity, quantityValid, persona, paywallConfig],
  );

  const locale = i18n.language === 'pt_BR' ? 'pt-BR' : (i18n.language || 'pt-BR');
  const money = useCallback(
    (amount: number, currency: string) =>
      new Intl.NumberFormat(locale, { style: 'currency', currency: currency.toUpperCase() }).format(amount),
    [locale],
  );
  const periodLabel = (cycle: BillingCycle) => (cycle === 'monthly' ? t('periodMonth') : t('periodYear'));

  const configUnavailable = paywallConfigLoading || !!paywallConfigError || !paywallConfig;
  const confirmDisabled =
    !state.breakdown ||
    submitting ||
    (feature === 'hours' && billing === 'monthly' && configUnavailable);

  const adjustQuantity = (delta: number) => {
    const base = Number.isInteger(parsedQuantity) ? parsedQuantity : Math.max(1, initialQuantity);
    setQuantityTouched(true);
    setQuantity(String(Math.min(MAX_LICENSES, Math.max(1, base + delta))));
  };

  const chooseProduct = (item: PaywallFeature) => {
    setFeature(item);
    setDetailsOpen(false);
    setPhase('configure');
  };

  // Returning to phase 1 must not throw away the quantity already given, and
  // never re-submits the registration — this surface lives past that commit.
  const changeProduct = () => {
    setDetailsOpen(false);
    setPhase('product');
  };

  const confirm = async () => {
    if (!state.plan || !state.breakdown || confirmDisabled) return;
    setSubmitting(true);
    try {
      await onConfirm({
        feature,
        billing,
        plan: state.plan,
        quantity: state.quantity,
        breakdown: state.breakdown,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Stripe is the authority on what a product is called (#304). The local
  // productOption_* copy is a fallback for when the catalog has no name —
  // never a second, drifting source of truth.
  const productTitle = (item: PaywallFeature) =>
    productNaming(catalog, item).name ?? t(`productOption_${item}`);
  const productShort = (item: PaywallFeature) =>
    productNaming(catalog, item).description ?? t(`productOption_${item}Short`);

  if (catalogLoading) {
    return {
      phase,
      selectedFeature: feature,
      content: (
        <View style={styles.centered} testID="pricing-loading">
          <ActivityIndicator accessibilityLabel={t('loading')} />
        </View>
      ),
      footer: null,
    };
  }

  if (catalogError || available.length === 0) {
    return {
      phase,
      selectedFeature: feature,
      content: (
        <View style={styles.centered} testID="pricing-error">
          <Text accessibilityRole="alert" style={{ color: theme.colors.error, textAlign: 'center' }}>
            {t('planUnavailable')}
          </Text>
          {onRetryCatalog && (
            <Button mode="outlined" onPress={onRetryCatalog} testID="pricing-retry">
              {t('tryAgain')}
            </Button>
          )}
        </View>
      ),
      footer: null,
    };
  }

  // ── Phase 1 — choose the product ──────────────────────────────────────
  if (phase === 'product') {
    return {
      phase,
      selectedFeature: feature,
      footer: null,
      content: (
        <View
          style={[styles.cards, narrow ? styles.cardsColumn : styles.cardsRow]}
          accessibilityRole="radiogroup"
          testID="pricing-phase-product"
        >
          {available.map(item => {
            const summary = summariseForPhaseOne(catalog, item);
            const selected = feature === item && phase !== 'product';
            return (
              <Pressable
                key={item}
                accessibilityRole="radio"
                accessibilityState={{ selected }}
                accessibilityLabel={productTitle(item)}
                onPress={() => chooseProduct(item)}
                testID={`product-option-${item}`}
                style={[
                  styles.card,
                  narrow ? styles.cardFull : styles.cardThird,
                  { backgroundColor: theme.colors.surface, borderColor: theme.colors.outline },
                ]}
              >
                <Text variant="titleMedium" style={{ color: theme.colors.onSurface }}>
                  {productTitle(item)}
                </Text>
                <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>
                  {productShort(item)}
                </Text>

                {summary.monthlyAmount != null && (
                  <Text
                    variant="titleSmall"
                    testID={`product-price-${item}-monthly`}
                    style={{ color: theme.colors.onSurface }}
                  >
                    {summary.annualMonthlyEquivalent != null
                      ? t('productCardPricing', {
                          monthly: money(summary.monthlyAmount, summary.currency),
                          annualMonthly: money(summary.annualMonthlyEquivalent, summary.currency),
                        })
                      : t('productCardPricingMonthlyOnly', {
                          monthly: money(summary.monthlyAmount, summary.currency),
                        })}
                  </Text>
                )}
                {summary.annualBilledTotal != null && (
                  <Text
                    variant="bodySmall"
                    testID={`product-price-${item}-annual`}
                    style={{ color: theme.colors.onSurfaceVariant }}
                  >
                    {t('productCardAnnualBilled', {
                      total: money(summary.annualBilledTotal, summary.currency),
                    })}
                  </Text>
                )}
              </Pressable>
            );
          })}
        </View>
      ),
    };
  }

  // ── Phase 2 — configure the chosen product ────────────────────────────
  const { breakdown, trial } = state;

  const content = (
    <View style={styles.configure} testID="pricing-phase-configure">
      <View style={styles.chosenRow}>
        <Text variant="titleMedium" style={{ color: theme.colors.onSurface }} testID="pricing-chosen-product">
          {productTitle(feature)}
        </Text>
        <Button
          mode="text"
          compact
          onPress={changeProduct}
          testID="pricing-change-product"
          contentStyle={styles.touchTarget}
        >
          {t('changeProduct')}
        </Button>
      </View>

      {cycles.length > 1 && (
        <View style={styles.cycleRow} testID="pricing-cycle" accessibilityRole="radiogroup">
          {cycles.map(cycle => {
            const active = billing === cycle;
            const label = cycle === 'monthly' ? t('planMonthlyTitle') : t('planAnnualTitle');
            return (
              <Button
                key={cycle}
                mode={active ? 'contained' : 'outlined'}
                onPress={() => setBilling(cycle)}
                style={styles.cycleButton}
                contentStyle={styles.touchTarget}
                accessibilityRole="radio"
                accessibilityState={{ selected: active }}
                accessibilityLabel={label}
                testID={`billing-option-${cycle}`}
              >
                {label}
              </Button>
            );
          })}
        </View>
      )}

      {billing === 'annual' && breakdown && breakdown.annualSavingUnitAmount > 0 && (
        <Text variant="bodyMedium" style={{ color: theme.colors.primary }} testID="pricing-annual-saving">
          {t('planAnnualSaving', {
            save: money(breakdown.annualSavingUnitAmount, breakdown.currency),
          })}
        </Text>
      )}

      {persona === 'owner' ? (
        <View style={styles.quantityBlock} testID="pricing-quantity">
          <Text variant="labelLarge" style={{ color: theme.colors.onSurface }}>
            {t('licensesInputLabel')}
          </Text>
          <View style={styles.quantityRow}>
            <IconButton
              icon="minus"
              mode="outlined"
              size={24}
              onPress={() => adjustQuantity(-1)}
              disabled={!quantityValid || parsedQuantity <= 1}
              accessibilityLabel={t('licensesDecrease')}
              testID="licenses-decrease"
            />
            <TextInput
              mode="outlined"
              value={quantity}
              onChangeText={value => {
                setQuantityTouched(true);
                setQuantity(value.replace(/[^0-9]/g, ''));
              }}
              keyboardType="number-pad"
              accessibilityLabel={t('licensesInputLabel')}
              testID="licenses-input"
              style={styles.quantityInput}
            />
            <IconButton
              icon="plus"
              mode="outlined"
              size={24}
              onPress={() => adjustQuantity(1)}
              disabled={quantityValid && parsedQuantity >= MAX_LICENSES}
              accessibilityLabel={t('licensesIncrease')}
              testID="licenses-increase"
            />
          </View>
          {!quantityValid && (
            <Text accessibilityRole="alert" style={{ color: theme.colors.error }}>
              {t('licensesInvalid', { max: MAX_LICENSES })}
            </Text>
          )}
        </View>
      ) : (
        <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }} testID="pricing-driver-single">
          {t('driverSingleLicense')}
        </Text>
      )}

      {referralPartner && (
        <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }} testID="pricing-referral">
          {t('referralAttribution', { partner: referralPartner })}
        </Text>
      )}

      {trial && breakdown && (
        <View style={styles.trialBlock}>
          <TrialTimeline trialDays={trial.days} />
          <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>
            {trial.requiresCard
              ? t('trialCardUpfront', { days: trial.days })
              : t('licensesTrialNoCard', { days: trial.days })}
          </Text>
        </View>
      )}

      {breakdown && (
        <>
          <Button
            mode="text"
            compact
            onPress={() => setDetailsOpen(open => !open)}
            testID="pricing-toggle-details"
            contentStyle={styles.touchTarget}
            accessibilityState={{ expanded: detailsOpen }}
          >
            {detailsOpen ? t('hideDetails') : t('viewDetails')}
          </Button>
          {detailsOpen && (
            <View style={styles.details} testID="pricing-details">
              <Text variant="bodyMedium" style={{ color: theme.colors.onSurfaceVariant }}>
                {t('reviewRecurringCalculation', {
                  quantity: state.quantity,
                  unit: money(breakdown.recurringUnitAmount, breakdown.currency),
                  total: money(breakdown.recurringTotal, breakdown.currency),
                  period: periodLabel(billing),
                })}
              </Text>
              {breakdown.setupTotal > 0 && (
                <Text variant="bodyMedium" style={{ color: theme.colors.onSurfaceVariant }}>
                  {t('reviewSetupCalculation', {
                    quantity: state.quantity,
                    unit: money(breakdown.setupUnitAmount, breakdown.currency),
                    total: money(breakdown.setupTotal, breakdown.currency),
                  })}
                </Text>
              )}
              <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>
                {trial
                  ? t('reviewNextChargeAfterTrial', {
                      total: money(breakdown.nextCharge, breakdown.currency),
                      days: trial.days,
                    })
                  : t('reviewNextCharge', {
                      total: money(breakdown.nextCharge, breakdown.currency),
                      period: periodLabel(billing),
                    })}
              </Text>
              <Divider />
              <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>
                {t('vatIncluded')}
              </Text>
            </View>
          )}
        </>
      )}
    </View>
  );

  const footer = (
    <View style={styles.footer} testID="pricing-footer">
      {breakdown && (
        <Text
          variant="titleMedium"
          testID="pricing-total-amount"
          style={{ color: theme.colors.primary }}
        >
          {t('reviewChargedToday', { total: money(breakdown.chargedToday, breakdown.currency) })}
        </Text>
      )}
      <Button
        mode="contained"
        onPress={confirm}
        loading={submitting}
        disabled={confirmDisabled}
        style={styles.confirm}
        contentStyle={styles.touchTarget}
        testID={confirmTestID}
      >
        {trial ? t('startTrialCta') : t('continueToPayment')}
      </Button>
    </View>
  );

  return { phase, selectedFeature: feature, content, footer };
}

const styles = StyleSheet.create({
  cards: { gap: 12, width: '100%' },
  cardsRow: { flexDirection: 'row', alignItems: 'stretch' },
  cardsColumn: { flexDirection: 'column' },
  card: { borderRadius: 12, borderWidth: 1, gap: 6, padding: 16, minHeight: 44, justifyContent: 'center' },
  cardFull: { width: '100%' },
  cardThird: { flex: 1 },
  configure: { gap: 14, width: '100%' },
  cycleRow: { flexDirection: 'row', gap: 8 },
  cycleButton: { borderRadius: 8, flex: 1 },
  chosenRow: { alignItems: 'center', flexDirection: 'row', gap: 8, justifyContent: 'space-between' },
  quantityBlock: { gap: 8 },
  quantityRow: { alignItems: 'center', flexDirection: 'row', gap: 8, justifyContent: 'center' },
  quantityInput: { textAlign: 'center', width: 120 },
  trialBlock: { gap: 6 },
  details: { gap: 6 },
  footer: { gap: 8, width: '100%' },
  confirm: { borderRadius: 10 },
  touchTarget: { minHeight: 44 },
  centered: { alignItems: 'center', gap: 12, justifyContent: 'center', minHeight: 120, padding: 16 },
});

export default useProductPricingSurface;
